import { makeStyles } from '@material-ui/core';
import { CheckCircle, KeyboardArrowRight, Refresh } from '@material-ui/icons';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { Save } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { motions, ProgressTracker } from '../../global';
import { Complete } from './Complete';
import { Details } from './Details';
import { Schedule } from './Schedule';
import { Tasks } from './Tasks';

const useProjectSchedulerClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        color: theme.colors.neutral[1],
    },
});

/**
 * The project schedule and task review component.
 * This component can be used to schedule new or existing projects (only ready to inspect projects).
 * At the end of the process, the project will be sent (created or updated) to the backend automatically.
 * Any changes are also committed before the update.
 * All callbacks except onComplete are fired in a useLayoutEffect, so re-renders can be executed before paint.
 *
 * @param props.onStart callback triggered when the component is rendered for the first time, it passes the `next`
 *        function and the number of `steps` in the scheduling process
 * @param props.onStep callback triggered after on start, after complete, and after each step scheduling step
 * @param props.onUnlocked callback triggered after on start and when the unlocked status change
 * @param props.onComplete callback triggered after the scheduling process is completed, the update is not ensured
 * @param props.completeActions list of actions to be displayed in the complete card
 */
export const ProjectScheduler = (props: {
    onStart?: (next: () => void, steps: number) => void;
    onStep?: (index: number) => void;
    onUnlocked?: (unlocked: boolean) => void;
    onComplete?: () => void;
    completeActions?: { name?: string; onClick?: () => void; primary?: boolean; fail?: boolean }[];
}) => {
    const classes = useProjectSchedulerClasses();
    const dispatch = useTypedDispatch();
    const { project, asset, plan } = useTypedSelector(state => ({
        asset: state.asset.changed,
        plan: state.plan.changed,
        project: state.project.changed,
    }));
    const StepComponents = [Details, Schedule, Tasks];
    const unlocked = Object.values(project?.methods ?? {}).flatMap(t => Object.keys(t ?? {})).length > 0;
    const [step, next] = React.useReducer(i => (unlocked ? Math.min(i + 1, StepComponents.length) : i), 0);
    const StepComponent = StepComponents[step];

    React.useLayoutEffect(() => props.onStart?.(next, StepComponents.length), []);
    React.useLayoutEffect(() => void props.onStep?.(step), [step]);
    React.useLayoutEffect(() => void props.onUnlocked?.(unlocked), [unlocked]);
    React.useEffect(() => {
        dispatch(actions.project.commit());
        if (step < StepComponents.length) return;
        step === StepComponents.length && props.onComplete?.();
        dispatch(actions.project.update());
    }, [step]);

    if (!asset || !plan || !project) return <span>{'#asset or plan or project not in store'}</span>;

    return (
        <AnimatePresence exitBeforeEnter>
            <motion.div key={step} className={classes.root} {...motions.presence({ slide: 10 }, true)}>
                {step < 3 ? <StepComponent /> : <Complete completeActions={props.completeActions} />}
            </motion.div>
        </AnimatePresence>
    );
};

/**
 * Bar to track scheduling progress based on the global progress tracker.
 * This tracker is not present by default in the project scheduler, it should be managed by the parent component.
 *
 * @param props.steps total number of steps
 * @param props.step current step
 * @param props.unlocked if the next action is enabled
 * @param props.next function to advance to the next step
 */
export const ProjectSchedulerTracker = (props: {
    steps: number;
    step: number;
    unlocked: boolean;
    next?: () => void;
}) => {
    const { t } = usePrefixTranslation('project', { project: 'scheduler.ProjectScheduler' });
    const dispatch = useTypedDispatch();
    const { project } = useTypedSelector(state => ({ project: state.project }));

    if (!project.project) return <span>{'#project not in store'}</span>;

    return (
        <ProgressTracker
            title={t('tracker-title')}
            subtitle={t('tracker-subtitle', { step: props.step + 1, steps: props.steps })}
            completed={(props.step / (props.steps - 1)) * 100}
            actions={[
                {
                    hidden: project.changes.length === 0,
                    title: t('tracker-icon-discard'),
                    icon: <Refresh />,
                    onClick: () => dispatch(actions.project.discard()),
                },
                {
                    title: t('tracker-icon-save'),
                    icon: <Save />,
                    onClick: () => {}, // TODO
                },
            ]}
            button={{
                hidden: !props.unlocked,
                text: t(`tracker-button-${props.step + 1 < props.steps ? 'continue' : 'schedule'}`),
                icon: props.step + 1 < props.steps ? <KeyboardArrowRight /> : <CheckCircle />,
                onClick: props.next,
            }}
        />
    );
};
